export class BookCover extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    static get observedAttributes() {
        return [
            'title', 'author', 'back-text', 'back-image', 'book-style',
            'background-color', 'back-color', 'left-side-color', 'cover-image',
            'cover-image-position', 'cover-image-size', 'cover-overlay',
            'cover-overlay-color', 'left-side-title', 'left-side-author',
            'hover-effect', 'view'
        ];
    }

    connectedCallback() {
        this.render();
        this.attachEventListeners();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue !== newValue) {
            this.render();
        }
    }

    get title() { return this.getAttribute('title') || ''; }
    get author() { return this.getAttribute('author') || ''; }
    get backText() { return this.getAttribute('back-text') || ''; }
    get backImage() { return this.getAttribute('back-image') || ''; }
    get bookStyle() { return this.getAttribute('book-style') || 'book-1'; }
    get backgroundColor() { return this.getAttribute('background-color') || '#ff924a'; }
    get backColor() { return this.getAttribute('back-color') || this.backgroundColor; }
    get leftSideColor() { return this.getAttribute('left-side-color') || this.backgroundColor; }
    get coverImage() { return this.getAttribute('cover-image') || ''; }
    get coverImagePosition() { return this.getAttribute('cover-image-position') || 'center center'; }
    get coverImageSize() { return this.getAttribute('cover-image-size') || 'contain'; }
    get coverOverlay() { return this.getAttribute('cover-overlay') !== 'false'; }
    get coverOverlayColor() { return this.getAttribute('cover-overlay-color') || 'rgba(0,0,0,0.3)'; }
    get leftSideTitle() { return this.getAttribute('left-side-title') || ''; }
    get leftSideAuthor() { return this.getAttribute('left-side-author') || ''; }
    get hoverEffect() { return this.getAttribute('hover-effect') !== 'false'; }
    get view() { return this.getAttribute('view') || 'default'; }

    subText(text) {
        return text?.substring(0, 25) || '';
    }

    render() {
        const bookClasses = [
            this.bookStyle,
            `bk-view${this.view}`,
            this.view === 'default' && this.hoverEffect ? 'bk-bookdefault' : ''
        ].filter(Boolean).join(' ');

        const coverStyle = this.getCoverStyle();

        this.shadowRoot.innerHTML = `
      <style>
        ${this.getStyles()}
      </style>
      ${this.coverImage ? `
      <div class="bk-book ${bookClasses}" style="width: 277px;height: 370px;margin-bottom:20px">
        <div class="bk-front">
          <div class="bk-cover" style="${coverStyle}">
            <div class="bk-cover-content">
              <h2>
                <span class="bk-author">${this.author}</span>
                <span class="bk-title">${this.title}</span>
              </h2>
            </div>
          </div>
          <div class="bk-cover-back"></div>
        </div>

        <div class="bk-back" style="background-color: ${this.backColor}">
          <div class="bk-back-content">
            ${this.backImage ? `<img src="${this.backImage}" alt="${this.title} back cover">` : ''}
            ${this.backText ? `<p>${this.backText}</p>` : ''}
          </div>
        </div>

        <div class="bk-right"></div>
        <div class="bk-left" style="background-color: ${this.leftSideColor}">
          <h2>
            <span class="bk-author">${this.leftSideAuthor || this.author}</span>
            <span class="bk-title">${this.subText(this.leftSideTitle) || this.subText(this.title)}</span>
          </h2>
        </div>
        <div class="bk-top"></div>
        <div class="bk-bottom"></div>
      </div>
      ` : ''}
    `;
    }

    getCoverStyle() {
        let style = `background-color: ${this.backgroundColor};`;

        if (this.coverImage) {
            style += `
        background-image: url(${this.coverImage});
        background-repeat: no-repeat;
        background-position: ${this.coverImagePosition};
        background-size: ${this.coverImageSize};
      `;
        }

        return style;
    }

    attachEventListeners() {
        const book = this.shadowRoot.querySelector('.bk-book');
        if (!book) return;

        if (this.hoverEffect) {
            book.addEventListener('mouseenter', () => this.handleMouseEnter());
            book.addEventListener('mouseleave', () => this.handleMouseLeave());
        }

        book.addEventListener('click', () => {
            this.dispatchEvent(new CustomEvent('book-clicked'));
        });
    }

    handleMouseEnter() {
        this.dispatchEvent(new CustomEvent('book-hover', { detail: { state: 'enter' } }));
    }

    handleMouseLeave() {
        this.dispatchEvent(new CustomEvent('book-hover', { detail: { state: 'leave' } }));
    }

    showBack() {
        this.setAttribute('view', 'back');
    }

    showDefault() {
        this.setAttribute('view', 'default');
    }

    getStyles() {
        return `
      .bk-book {
        position: relative;
        width: 100%;
        height: 100%;
        transform-style: preserve-3d;
        transition: transform 0.5s;
        margin-bottom: 20px;
        cursor: pointer;
      }

      .bk-book.bk-bookdefault:hover {
        transform: rotate3d(0,1,0,35deg);
      }

      .bk-book > div,
      .bk-front > div {
        display: block;
        position: absolute;
      }

      .bk-front {
        transform-style: preserve-3d;
        transform-origin: 0% 50%;
        transition: transform 0.5s;
        transform: translate3d(0,0,20px);
        z-index: 10;
      }

      .bk-front > div {
        z-index: 1;
        backface-visibility: hidden;
        transform-style: preserve-3d;
      }

      .bk-front,
      .bk-back,
      .bk-front > div {
        width: 277px;
        height: 370px;
      }

      .bk-left,
      .bk-right {
        width: 40px;
        left: -20px;
      }

      .bk-top,
      .bk-bottom {
        width: 295px;
        height: 40px;
        top: -15px;
        backface-visibility: hidden;
      }

      .bk-back {
        transform: rotate3d(0,1,0,-180deg) translate3d(0,0,20px);
        box-shadow: 10px 10px 30px rgba(0,0,0,0.3);
        border-radius: 3px 0 0 3px;
      }

      .bk-cover-back {
        background-color: #000;
        transform: rotate3d(0,1,0,-179deg);
      }

      .bk-right {
        height: 370px;
        top: 5px;
        transform: rotate3d(0,1,0,90deg) translate3d(0,0,295px);
        backface-visibility: hidden;
        background-color: #fff;
      }

      .bk-left {
        height: 370px;
        transform: rotate3d(0,1,0,-90deg);
      }

      .bk-top {
        transform: rotate3d(1,0,0,90deg);
        background-color: #fff;
      }

      .bk-bottom {
        transform: rotate3d(1,0,0,-90deg) translate3d(0,0,390px);
        background-color: #fff;
      }

      .bk-book.bk-viewback {
        transform: translate3d(0,0,0px) rotate3d(0,1,0,180deg);
      }

      .bk-front > div {
        border-radius: 0 3px 3px 0;
        box-shadow: inset 4px 0 10px rgba(0, 0, 0, 0.1);
      }

      .bk-front:after {
        content: '';
        position: absolute;
        top: 1px;
        bottom: 1px;
        left: -1px;
        width: 1px;
        background-color: ${this.backgroundColor};
      }

      .bk-cover:after,
      .bk-back:after {
        content: '';
        position: absolute;
        top: 0;
        left: 10px;
        bottom: 0;
        width: 3px;
        background: rgba(0,0,0,0.06);
        box-shadow: 1px 0 3px rgba(255, 255, 255, 0.1);
      }

      .bk-back:after {
        left: auto;
        right: 10px;
      }

      .bk-cover {
        width: 100%;
        height: 100%;
        position: relative;
        border-radius: 0 3px 3px 0;
        overflow: hidden;
      }

      .bk-cover-content {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 30px;
        background: ${this.coverOverlay ? this.coverOverlayColor : 'transparent'};
        z-index: 2;
      }

      .bk-cover h2 {
        margin: 0;
        color: #fff;
        text-shadow: 0 -1px 0 rgba(0,0,0,0.1);
      }

      .bk-author {
        display: block;
        text-transform: uppercase;
        font-weight: 400;
        font-size: 13px;
        margin-bottom: 5px;
      }

      .bk-title {
        display: block;
        font-family: "Big Caslon", "Book Antiqua", "Palatino Linotype", Georgia, serif;
        font-size: 18px;
      }

      .bk-left h2 {
        width: 370px;
        height: 40px;
        transform-origin: 0 0;
        transform: rotate(90deg) translateY(-40px);
        color: #fff;
        font-size: 15px;
        line-height: 40px;
        padding-right: 10px;
        text-align: right;
        margin: 0;
      }

      .bk-left .bk-author {
        font-size: 11px;
        padding-right: 20px;
      }

      .bk-left .bk-title {
        font-size: 14px;
      }

      .bk-back-content {
        padding: 40px;
        text-align: center;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }

      .bk-back p {
        color: #fff;
        font-size: 13px;
        font-weight: 700;
        margin: 0;
      }

      .bk-back img {
        max-width: 200px;
        display: block;
        margin: 0 auto 20px;
      }

      .book-2 .bk-cover-content {
        background: rgba(0,0,0,0.7);
      }

      .book-2 .bk-cover h2,
      .book-2 .bk-left h2 {
        color: #fff;
        font-family: 'Arial', Georgia, serif;
        font-weight: 400;
      }

      .book-2 .bk-back p {
        color: red;
        font-size: 13px;
        font-family: "Big Caslon", "Book Antiqua", "Palatino Linotype", Georgia, serif;
      }

      .book-3 .bk-cover-content {
        position: absolute;
        top: 40px;
        bottom: auto;
        background: transparent;
        padding-top: 15px;
      }

      .book-3 .bk-back p {
        color: #fff;
        font-size: 13px;
        font-family: Georgia, Times, "Times New Roman", serif;
        text-shadow: 0 -1px 0 rgba(0,0,0,0.1);
      }
    `;
    }
}

